<template>
  <div class="order-confirmation-popup" v-if="!loading">
    <h2 class="title order-confirmation-popup__title">{{ $t("page.order.confirmation.summary") }}</h2>

    <div class="mb-20 pb-25 bb-grey">
      <label class="label mb-5">{{ $t("page.order.confirmation.deliveryaddress") }}</label>
      <div id="delivery-address">
        <template v-if="basicData.checkoutDisplayMode !== 'us'">
          <div class="value">{{ getAddressLine1(deliveryAddress) }}</div>
          <div class="value">{{ deliveryAddress.streetHouseNumber }}</div>
          <div class="value">
            {{ deliveryAddress.zipCode }} {{ deliveryAddress.city }}, {{ $t(deliveryAddress.countryTranslationKey) }}
          </div>
        </template>
        <template v-if="basicData.checkoutDisplayMode === 'us'">
          <div class="value">{{ getAddressLine1(deliveryAddress) }}</div>
          <div class="value">{{ deliveryAddress.streetHouseNumber }}</div>
          <div class="value">{{ deliveryAddress.city }}, {{ deliveryAddress.zipCode }}</div>
          <div class="value">{{ $t(deliveryAddress.countryTranslationKey) }}</div>
        </template>
      </div>
    </div>

    <div v-for="deliveryType in supportedDeliveryTypes" :key="deliveryType.id" class="mb-10">
      <label class="label">{{ $t(deliveryType.deliveryTypeOrderNumberTranslationKey) }}</label>
      <div class="value">{{ basicData.temporaryOrderNumber }} {{ deliveryType.orderNumberPostfix }}</div>
    </div>

    <div class="mb-30">
      <label class="label">{{ $t("page.order.confirmation.onholdordernumber") }}</label>
      <div class="value">{{ basicData.temporaryOrderNumber }} H</div>
    </div>

    <div class="mb-10">
      <label class="label">{{ $t("page.order.confirmation.referencenumber") }}</label>
      <div id="reference-number" class="value">{{ referenceNumber || "-" }}</div>
    </div>

    <div>
      <label class="label">{{ $t("page.order.confirmation.machinenumber") }}</label>
      <div id="machine-number" class="value">{{ selectedMachine ? selectedMachine.name : "-" }}</div>
    </div>

    <div v-if="purchaseDocument" class="mt-10">
      <label class="label">{{ $t("page.order.confirmation.purchasedocumentname") }}</label>
      <a id="purchase-document-name" class="value" @click.prevent="downloadPurchaseDocument">
        {{ purchaseDocument.name }}
      </a>
    </div>

    <div class="order-confirmation-popup__total">
      <div class="mb-20 pb-25 bb-grey">
        <div class="title">On hold</div>
        <div id="order-hold-price">+{{ formatPrice(onHoldTotalPrice) }}</div>
      </div>
      <div>
        <div class="title">Ready For Production</div>
        <div id="order-production-price" class="price">+{{ formatPrice(onProductionTotalPrice) }}</div>
        <div class="desc">Exkl. MwSt.</div>
      </div>
    </div>

    &nbsp;
      <div class="order-confirmation-popup__summary-terms">
        <span>{{ $t("page.order.confirmation.terms-agreement-text") }}</span>
        <div v-on:@click="downloadTermsAndConditionsPDF">
          <a href="#" @click="downloadTermsAndConditionsPDF">{{$t("page.order.confirmation.terms-and-conditions")}}</a>
        </div>
        <div v-html="privacyPolicy"></div>
      </div>
    &nbsp;

    <v-button id="request-now-button" class="order-confirmation-popup__button" primary @click="saveOrder">
      <img src="~@/assets/icons/icon-request.svg" class="icon-left" />
      {{ $t("page.order.confirmation.requestnow") }}
    </v-button>
  </div>
  <img v-else src="~@/assets/icons/loading-indicator.gif" class="absolute-center" width="55" />
</template>

<script>
import { mapGetters } from "vuex";
import { marked } from "marked";
import { VButton } from "shared";
import * as OrderDAO from "@/dao/order-dao";
import * as ObjectUtils from "@/utils/ObjectUtils";
import * as DownloadUtils from "@/utils/DownloadUtils";

export default {
  components: { VButton },
  name: "VOrderConfirmationSummary",
  props: {
    deliveryAddress: {
      type: Object,
      default: null,
    },
    purchaseDocument: {
      type: File,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
    };
  },
  computed: {
    ...mapGetters("order", [
      "selectedMachine",
      "referenceNumber",
      "onProductionElements",
      "onHold",
      "formatPrice",
      "basicData",
      "freeContingent",
      "orderList",
      "deliveryTypes",
    ]),

    supportedDeliveryTypes() {
      return this.deliveryTypes.filter((dt) => dt.displayOrderNumber);
    },

    onHoldTotalPrice() {
      const price = this.onHold.totalPrice;
      return price + (price * parseFloat(this.basicData.vatTaxRate)) / 100;
    },
    onProductionTotalPrice() {
      const price = this.freeContingent
        ? 0
        : this.onProductionElements.reduce((price, p) => price + p.canisters * parseFloat(p.netPrice), 0);
      return price + (price * parseFloat(this.basicData.vatTaxRate)) / 100;
    },

    price() {
      return this.onHoldTotalPrice + this.onProductionTotalPrice;
    },

    privacyPolicy() {
      const raw = this.$t("page.order.confirmation.privacy-policy");
      const html = marked(raw);
      return html.replace('<a ', '<a target="_blank" ');
    },

    termsAndConditions(){
      const raw = this.$t("page.order.confirmation.terms-and-conditions");
      const html = marked(raw);
      return html.replace('<a ', '<a target="_blank" ');
    },


  },
  methods: {
    async downloadTermsAndConditionsPDF()
    {
      const { termsAndConditionsUrl } = await OrderDAO.getTermsAndConditions();
      await DownloadUtils.downloadFile(termsAndConditionsUrl, "canister_terms_and_conditions.pdf");
    },
    getAddressLine1(address) {
      return `${address.name1} ${address.name2}${address.nameSuffix.length ? `, ${address.nameSuffix}` : ""}`;
    },
    async downloadPurchaseDocument() {
      const url = await DownloadUtils.loadAsDataURL(this.purchaseDocument);
      await DownloadUtils.downloadFile(url, this.purchaseDocument.name);
    },
    async saveOrder() {
      this.loading = true;
      const response = await OrderDAO.saveOrder(
        {
          referenceNumber: this.referenceNumber,
          machineId: ObjectUtils.getProperty(this.selectedMachine, "id", null),
          deliveryAddressId: this.deliveryAddress.id,
          freeContingent: this.freeContingent,
          orderPositions: this.orderList.map((item) => ({
            id: item.id,
            medKey: item.medKey,
            name: item.name,
            assemblyArticleNumber: item.uvResistant
              ? item.canisterSize === "small"
                ? item.assemblyArticleNumberSmallUvResistant
                : item.assemblyArticleNumberLargeUvResistant
              : item.canisterSize === "small"
              ? item.assemblyArticleNumberSmallNonUvResistant
              : item.assemblyArticleNumberLargeNonUvResistant,
            rotorArticleNumber: item.rotorArticleNumber,
            deliveryTypeId: item.deliveryTypeId,
            canisterSize: item.canisterSize,
            uvResistant: item.uvResistant,
            amountOfCanisters: item.amountOfCanisters,
            isOnHold: item.isOnHold,
          })),
        },
        this.purchaseDocument
      );
      this.loading = false;
      this.$emit("saved", response);
    },
  },

};
</script>

<style lang="scss" scoped>
@import "../../styles/variables";
.order-confirmation-popup {
  overflow: hidden;
  &__title {
    font-size: 24px;
    line-height: 26px;
    margin: 56px 0 23px 0;
    letter-spacing: 0.7px;
  }
  &__total {
    width: 200px;
    float: right;
    text-align: right;
    margin: -165px 0 20px 0;
    letter-spacing: 0.5px;
    .title {
      font-size: 13px;
      margin-bottom: 10px;
    }
    .price {
      font-size: 24px;
      line-height: 26px;
      margin: 6px 0;
    }
    .desc {
      font-size: 13px;
      color: #999999;
    }
  }
  &__button {
    float: right;
    margin-top: 30px;
    padding: 7px 16px 8px 23px;
    letter-spacing: 0.4px;
    .icon-left {
      margin: 0 8px 0 -9px !important;
    }
  }
  &__summary-terms {
    padding-top: 1rem;
  }
}
.label {
  color: #999999;
  font-size: 13px;
  display: block;
}
.value {
  font-size: 14px;
}
a:hover {
  text-decoration: underline;
}
.order-confirmation-popup__summary-terms a {
  color: blue;
  text-decoration: underline;
}

</style>
